import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import UserAgreement from './UserAgreement';
import './Auth.css';

const Login = () => {
  const navigate = useNavigate();
  const { login, loginWithEmail, sendCode, guestLogin } = useAuth();
  
  const [loginType, setLoginType] = useState('password'); // 'password' or 'email'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [captcha, setCaptcha] = useState(''); // 图形验证码
  const [captchaText, setCaptchaText] = useState(''); // 生成的验证码文本
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  
  const canvasRef = useRef(null);

  // 生成随机验证码
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let text = '';
    for (let i = 0; i < 4; i++) {
      text += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(text.toLowerCase());
    return text;
  };

  // 绘制验证码
  const drawCaptcha = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const text = generateCaptcha();
    
    // 清空画布
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // 绘制干扰线
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgb(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255})`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }
    
    // 绘制验证码文字
    ctx.font = 'bold 24px Arial';
    ctx.textBaseline = 'middle';
    
    for (let i = 0; i < text.length; i++) {
      const x = 10 + i * 28;
      const y = canvas.height / 2 + (Math.random() * 10 - 5);
      const rotate = (Math.random() * 30 - 15) * Math.PI / 180;
      
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotate);
      ctx.fillStyle = `rgb(${Math.random() * 100}, ${Math.random() * 100}, ${Math.random() * 100})`;
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }
    
    // 绘制干扰点
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgb(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255})`;
      ctx.beginPath();
      ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, 1, 0, 2 * Math.PI);
      ctx.fill();
    }
  };

  // 初始化验证码
  useEffect(() => {
    drawCaptcha();
  }, []);

  // 切换登录方式时重置验证码
  useEffect(() => {
    setCaptcha('');
    drawCaptcha();
  }, [loginType]);

  // 发送验证码
  const handleSendCode = async () => {
    if (!email) {
      setError('请输入邮箱');
      return;
    }
    
    if (!captcha) {
      setError('请输入图形验证码');
      return;
    }
    
    if (captcha.toLowerCase() !== captchaText) {
      setError('图形验证码错误');
      drawCaptcha();
      setCaptcha('');
      return;
    }

    setCodeLoading(true);
    setError('');

    try {
      await sendCode(email, 'login');
      // 开始倒计时
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.detail || '发送验证码失败');
      drawCaptcha();
    } finally {
      setCodeLoading(false);
    }
  };

  // 游客登录
  const handleGuestLogin = () => {
    guestLogin();
    navigate('/');
  };

  // 登录
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreed) {
      setError('请先阅读并同意用户服务协议');
      return;
    }
    
    // 验证图形验证码
    if (!captcha) {
      setError('请输入图形验证码');
      return;
    }
    
    if (captcha.toLowerCase() !== captchaText) {
      setError('图形验证码错误');
      drawCaptcha();
      setCaptcha('');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      if (loginType === 'password') {
        await login(username, password);
      } else {
        await loginWithEmail(email, code);
      }
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || '登录失败');
      drawCaptcha();
      setCaptcha('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2 className="auth-title">登录</h2>
        
        <div className="login-type-switch">
          <button
            className={`switch-btn ${loginType === 'password' ? 'active' : ''}`}
            onClick={() => setLoginType('password')}
          >
            密码登录
          </button>
          <button
            className={`switch-btn ${loginType === 'email' ? 'active' : ''}`}
            onClick={() => setLoginType('email')}
          >
            验证码登录
          </button>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          {loginType === 'password' ? (
            <>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="用户名或邮箱"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  placeholder="密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group captcha-group">
                <input
                  type="text"
                  placeholder="图形验证码"
                  value={captcha}
                  onChange={(e) => setCaptcha(e.target.value)}
                  maxLength={4}
                  required
                />
                <canvas
                  ref={canvasRef}
                  width={120}
                  height={40}
                  className="captcha-canvas"
                  onClick={drawCaptcha}
                  title="点击刷新验证码"
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <input
                  type="email"
                  placeholder="邮箱"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group captcha-group">
                <input
                  type="text"
                  placeholder="图形验证码"
                  value={captcha}
                  onChange={(e) => setCaptcha(e.target.value)}
                  maxLength={4}
                  required
                />
                <canvas
                  ref={canvasRef}
                  width={120}
                  height={40}
                  className="captcha-canvas"
                  onClick={drawCaptcha}
                  title="点击刷新验证码"
                />
              </div>
              <div className="form-group code-group">
                <input
                  type="text"
                  placeholder="邮箱验证码"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={6}
                  required
                />
                <button
                  type="button"
                  className="code-btn"
                  onClick={handleSendCode}
                  disabled={countdown > 0 || codeLoading}
                >
                  {codeLoading ? '发送中...' : countdown > 0 ? `${countdown}s` : '获取验证码'}
                </button>
              </div>
            </>
          )}

          <div className="agreement-check">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span>我已阅知并同意</span>
            </label>
            <button 
              type="button" 
              className="agreement-link"
              onClick={() => setShowAgreement(true)}
            >
              《用户服务协议》
            </button>
          </div>

          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? '登录中...' : '登录'}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/forgot-password">忘记密码？</Link>
          <Link to="/register">没有账号？立即注册</Link>
        </div>

        <div className="guest-login-section">
          <button 
            type="button" 
            className="guest-btn"
            onClick={handleGuestLogin}
          >
            🔍 游客模式
          </button>
          <p className="guest-hint">游客模式下可浏览页面，操作时需登录</p>
        </div>
      </div>

      <UserAgreement 
        show={showAgreement} 
        onClose={() => setShowAgreement(false)} 
      />
    </div>
  );
};

export default Login;
